import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { RevenueAllocationsParams } from '../types'

const today = dayjs()
const DEFAULT_GTE = today.startOf('quarter').format('YYYY-MM')
const DEFAULT_LTE = today.endOf('quarter').format('YYYY-MM')

// Serialize filters to URL params
const serializeFilters = (
    filters: RevenueAllocationsParams
): Record<string, string | undefined> => {
    return {
        gte: filters.gte !== DEFAULT_GTE ? filters.gte : undefined,
        lte: filters.lte !== DEFAULT_LTE ? filters.lte : undefined,
        is_actual: filters.is_actual_revenue ? 'true' : undefined,
        product_groups: filters.product_group_names?.length
            ? filters.product_group_names.join(',')
            : undefined,
        sale_managers: filters.sale_manager_codes?.length
            ? filters.sale_manager_codes.join(',')
            : undefined,
        account_managers: filters.account_manager_codes?.length
            ? filters.account_manager_codes.join(',')
            : undefined,
        is_paid:
            filters.is_paid !== undefined ? String(filters.is_paid) : undefined,
        is_allocated:
            filters.is_allocated !== undefined
                ? String(filters.is_allocated)
                : undefined,
    }
}

// Parse filters from URL params
const parseFiltersFromUrl = (
    searchParams: URLSearchParams
): RevenueAllocationsParams => {
    return {
        gte: searchParams.get('gte') || DEFAULT_GTE,
        lte: searchParams.get('lte') || DEFAULT_LTE,
        is_actual_revenue: searchParams.get('is_actual') === 'true',
        product_group_names: searchParams
            .get('product_groups')
            ?.split(',')
            .filter(Boolean),
        sale_manager_codes: searchParams
            .get('sale_managers')
            ?.split(',')
            .filter(Boolean)
            .map(Number),
        account_manager_codes: searchParams
            .get('account_managers')
            ?.split(',')
            .filter(Boolean)
            .map(Number),
        is_paid:
            searchParams.get('is_paid') === 'true'
                ? true
                : searchParams.get('is_paid') === 'false'
                  ? false
                  : undefined,
        is_allocated:
            searchParams.get('is_allocated') === 'true'
                ? true
                : searchParams.get('is_allocated') === 'false'
                  ? false
                  : undefined,
    }
}

interface UseRevenueFiltersOptions {
    syncToUrl?: boolean
}

/**
 * Hook to manage revenue filters with URL sync
 * Allows sharing filters between tabs via URL params
 */
export const useRevenueFilters = (
    options: UseRevenueFiltersOptions = { syncToUrl: true }
) => {
    const { syncToUrl = true } = options
    const [searchParams, setSearchParams] = useSearchParams()

    // Parse initial values from URL (only on mount)
    const initialFilters = useMemo(
        () => parseFiltersFromUrl(searchParams),

        []
    )

    const [filters, setFilters] =
        useState<RevenueAllocationsParams>(initialFilters)

    // Sync state to URL params (preserve other params like tab)
    const updateUrlParams = useCallback(
        (updates: Record<string, string | undefined>) => {
            if (!syncToUrl) return
            setSearchParams(
                (prev) => {
                    const newParams = new URLSearchParams(prev)
                    Object.entries(updates).forEach(([key, value]) => {
                        if (
                            value !== undefined &&
                            value !== '' &&
                            value !== 'false'
                        ) {
                            newParams.set(key, value)
                        } else {
                            newParams.delete(key)
                        }
                    })
                    return newParams
                },
                { replace: true }
            )
        },
        [setSearchParams, syncToUrl]
    )

    // Sync filters to URL
    useEffect(() => {
        updateUrlParams(serializeFilters(filters))
    }, [filters, updateUrlParams])

    // Handle filters change
    const handleFiltersChange = useCallback(
        (newFilters: RevenueAllocationsParams) => {
            setFilters(newFilters)
        },
        []
    )

    return {
        filters,
        setFilters: handleFiltersChange,
        updateUrlParams,
        DEFAULT_GTE,
        DEFAULT_LTE,
    }
}

export default useRevenueFilters
